import React from 'react';
import { ArcDroneMarker } from '@src/core/controllers';
import { getFlightHeading } from '@src/features/order/provider/flight-provider/flight-position-cache';
import { DroneEntity } from '@src/core/features';
import { DroneMarkersListProps } from './types';

interface IdleRowProps {
  drone: DroneEntity;
  isSelected: boolean;
  onClick: (drone: DroneEntity) => void;
}

const IdleDroneRow = React.memo<IdleRowProps>(({ drone, isSelected, onClick }) => {
  const position = React.useMemo(
    () => ({ lat: Number(drone.latitude), lng: Number(drone.longitude) }),
    [drone.latitude, drone.longitude]
  );
  const handleClick = React.useCallback(() => onClick(drone), [onClick, drone]);

  if (isSelected) {
    return <ArcDroneMarker.idleSelected position={position} onClick={handleClick} />;
  }
  return <ArcDroneMarker.idleArrow position={position} onClick={handleClick} />;
});
IdleDroneRow.displayName = 'IdleDroneRow';

interface FlyingRowProps {
  drone: DroneEntity;
  rotation: number;
  isSelected: boolean;
  onClick: (drone: DroneEntity) => void;
}

const FlyingDroneRow = React.memo<FlyingRowProps>(({ drone, rotation, isSelected, onClick }) => {
  const position = React.useMemo(
    () => ({ lat: Number(drone.latitude), lng: Number(drone.longitude) }),
    [drone.latitude, drone.longitude]
  );
  const handleClick = React.useCallback(() => onClick(drone), [onClick, drone]);

  if (isSelected) {
    return (
      <ArcDroneMarker.missionSelected position={position} onClick={handleClick} rotation={rotation} />
    );
  }
  return (
    <ArcDroneMarker.missionArrow position={position} onClick={handleClick} rotation={rotation} />
  );
});
FlyingDroneRow.displayName = 'FlyingDroneRow';

/**
 * Component to render list of drone markers (idle and flying drones)
 * Handles both idle and mission drone markers with rotation support
 */
export const DroneMarkersList: React.FC<DroneMarkersListProps> = React.memo(({
  idleDrones,
  flyingDrones,
  clickedDrone,
  hoveredDrone,
  rotations,
  onDroneClick,
}) => {
  return (
    <>
      {idleDrones.map((drone) => {
        const isSelected =
          drone.serialNumber === clickedDrone?.serialNumber ||
          drone.serialNumber === hoveredDrone?.serialNumber;
        return (
          <IdleDroneRow
            key={drone.id}
            drone={drone}
            isSelected={isSelected}
            onClick={onDroneClick}
          />
        );
      })}
      {flyingDrones.map((drone) => {
        const rotation = rotations[drone.serialNumber] ?? getFlightHeading(drone.serialNumber) ?? 0;
        const isClicked = drone.serialNumber === clickedDrone?.serialNumber;
        const isHovered = drone.serialNumber === hoveredDrone?.serialNumber;

        // Show selected marker if clicked or hovered, otherwise show normal
        if (isClicked || isHovered) {
          return (
            <ArcDroneMarker.missionSelected
              key={drone.id}
              position={{ lat: Number(drone.latitude), lng: Number(drone.longitude) }}
              onClick={() => onDroneClick(drone)}
              rotation={rotation}
            />
          );
        }

        const isSelected =
          drone.serialNumber === clickedDrone?.serialNumber ||
          drone.serialNumber === hoveredDrone?.serialNumber;
        return (
          <FlyingDroneRow
            key={drone.id}
            drone={drone}
            rotation={rotation}
            isSelected={isSelected}
            onClick={onDroneClick}
          />
        );
      })}
    </>
  );
});

DroneMarkersList.displayName = 'DroneMarkersList';
